<template>
  <gp-navigation v-model:is-opened="navOpened">
    <template #nav-body>
      <div class="mb-8 w-52">
        <a href="/">
          <img src="../assets/logo.svg" alt="GPortal logo" />
        </a>
      </div>
      <gp-navigation-group :hide-group-label="true">
        <template #nav-group-links>
          <gp-navigation-item>
            <gp-navigation-item-link :href="gamecloudUrl">
              <font-awesome-icon class="text-gp-rhino mr-2 min-w-6" :icon="faCircleArrowLeft" />
              <span class="font-bold uppercase">{{ t("general/menu/back-to-dashboard") }}</span>
            </gp-navigation-item-link>
          </gp-navigation-item>
        </template>
      </gp-navigation-group>
      <gp-navigation-group v-for="subMenu in subMenus" :key="subMenu.name">
        <template #nav-group-header>
          {{ t(`ngp/menu_section/${subMenu.name}`) }}
        </template>
        <template #nav-group-links>
          <gp-navigation-item v-for="menuItem in subMenu.items" :key="menuItem.route">
            <router-link v-slot="{ href, navigate }" :to="{ params: { tabSegments: [menuItem.route] } }" custom>
              <gp-navigation-item-link
                :href="href"
                :label="t(menuItem.labelKey ?? `ngp/route/${menuItem.route}`)"
                :icon="menuItem.icon"
                :is-active="menuItem.isActive"
                :disabled="disableMenu"
                :class="{ 'pointer-events-auto cursor-not-allowed': disableMenu }"
                :data-testid="
                  disableMenu
                    ? dataCyName(`sidebar-${menuItem.route}-disabled`)
                    : dataCyName(`sidebar-${menuItem.route}`)
                "
                @click="(ev: MouseEvent) => handleClick(ev, navigate)"
              />
            </router-link>
          </gp-navigation-item>
        </template>
      </gp-navigation-group>
      <gp-navigation-group>
        <template #nav-group-header>
          {{ t("ngp/menu_section/help") }}
        </template>
        <template #nav-group-links>
          <gp-navigation-item>
            <gp-navigation-item-link
              :label="t('ngp/side_bar/help/support')"
              :icon="faHeadset"
              :href="supportUrl"
              target="_blank"
              @click="navOpened = false"
            />
          </gp-navigation-item>
          <gp-navigation-item>
            <gp-navigation-item-link
              :label="t('ngp/side_bar/help/wiki')"
              :icon="faBookOpen"
              :href="wikiUrl"
              target="_blank"
              @click="navOpened = false"
            />
          </gp-navigation-item>
          <gp-navigation-item>
            <gp-navigation-item-link
              label="Discord"
              :icon="faDiscord"
              :href="discordUrl"
              target="_blank"
              @click="navOpened = false"
            />
          </gp-navigation-item>
        </template>
      </gp-navigation-group>
    </template>
    <template #nav-footer>
      <div class="flex items-start justify-start gap-6 px-4" :class="isAdmin ? 'pb-20' : 'pb-8'">
        <button
          class="flex flex-col items-center gap-2"
          type="button"
          :aria-label="t('ngp/side_bar/logout')"
          @click="logout"
        >
          <span
            class="flex h-10 w-10 items-center justify-center rounded-full bg-transparent transition-colors duration-300 hover:bg-black/10"
          >
            <font-awesome-icon class="text-gp-rhino h-6 w-6" :icon="faSignOutAlt" />
          </span>
          <span class="m-0 capitalize">{{ t("ngp/side_bar/logout") }}</span>
        </button>

        <a
          class="flex flex-col items-center gap-2"
          :href="profileUrl"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="userName || t('ngp/side_bar/profile')"
        >
          <span
            class="flex h-10 w-10 items-center justify-center rounded-full bg-transparent transition-colors duration-300 hover:bg-black/10"
          >
            <profile-icon class="h-6 w-6 rounded-full" />
          </span>
          <span class="m-0">{{ userName || t("ngp/side_bar/profile") }}</span>
        </a>
      </div>
    </template>
  </gp-navigation>
</template>

<script setup lang="ts">
import { auth } from "@/keycloak";
import { useClientStore } from "@/store/client";
import { useServiceStore } from "@/store/service";
import dataCyName from "@/util/cy";
import { usePermissions } from "@/util/permissions";
import { useWiUtils } from "@/util/webinterface";
import { faDiscord } from "@fortawesome/free-brands-svg-icons";
import { faCircleArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import { faBookOpen, faHeadset, faSignOutAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { GpNavigation, GpNavigationGroup, GpNavigationItem, GpNavigationItemLink } from "@gportal/uikit";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import cleanStorage from "../util/storage";
import ProfileIcon from "./ProfileIcon.vue";
import { useMenu } from "./menu";

const client = useClientStore();
const service = useServiceStore();
const { activeMenuItem } = useMenu();
const { gamecloudUrl, homeUrl, supportUrl, wikiUrl, profileUrl, discordUrl } = useWiUtils();
const { isAdmin } = usePermissions();

const { t } = useI18n();

const navOpened = computed({
  get() {
    return client.showMobileNav;
  },
  set(value: boolean) {
    client.setMobileNav(value);
  },
});

function hideMobileNav() {}

function handleClick(ev: MouseEvent, navigate: (ev: MouseEvent) => void): void {
  if (disableMenu.value) {
    ev.preventDefault();
  } else {
    navOpened.value = false;
    navigate(ev);
  }
}

const linksDisabled = computed(() => service.interactivityLocked);
const interactivityLockVisibility = computed(() => client.interactivityLockVisibility);
const disableMenu = computed(() => linksDisabled.value && interactivityLockVisibility.value);
const menuData = computed(() => service.ctx.gameProfile?.menu);

const subMenus = computed(() =>
  menuData.value?.map((subMenu) => ({
    name: subMenu.name,
    items: subMenu.items.map((item) => ({
      route: item.route,
      icon: item.icon,
      component: item.component,
      labelKey: item.labelKey,
      isActive: activeMenuItem.value?.route == item.route,
    })),
  })),
);

// On Menu item change, hide mobile nav
watch(activeMenuItem, (_newMenuItem, _) => {
  hideMobileNav();
});

const userName = computed(() => client.me?.jwt.username);

function logout() {
  cleanStorage();
  auth.logout({ redirectUri: `${window.location.origin}${homeUrl.value}` });
}
</script>
